import { AnalyticsOutlined, DarkMode, ExitToApp, LightMode, HelpOutline, Star, Edit, Policy, Language, LockOutlined } from '@mui/icons-material';
import { Avatar, Button, Switch, Dialog, DialogTitle, DialogContent, DialogActions, TextField, CircularProgress, IconButton } from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useTranslation } from 'react-i18next';
import { firebaseGoogleSignOut, updateUserAuthProfile } from '../firebase/authHandler';
import { getPreferences, updatePreferences, getProfileData, updateProfileData, signInUpdate } from '../firebase/dataHandler';

export default function Profile({ user, setUser }) {
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [useHandedAmount, setUseHandedAmount] = useState(false);
  const [isPremium, setIsPremium] = useState(false);
  const [legalAcceptedVersion, setLegalAcceptedVersion] = useState(null);
  const [legalAcceptedAt, setLegalAcceptedAt] = useState(null);

  // Edit Profile State
  const [editOpen, setEditOpen] = useState(false);
  const [editName, setEditName] = useState(user?.displayName || '');
  const [editPhone, setEditPhone] = useState(user?.phoneNumber || '');
  const [isSaving, setIsSaving] = useState(false);
  
  const { i18n } = useTranslation();

  useEffect(() => {
    getPreferences().then(prefs => {
      setUseHandedAmount(prefs.useHandedAmountForAnalytics || false);
      setIsPremium(prefs.isPremium === true);
      if (prefs.legal) {
        setLegalAcceptedVersion(prefs.legal.legalAcceptedVersion);
        setLegalAcceptedAt(prefs.legal.legalAcceptedAt);
      }
    }).catch(console.error);

    getProfileData().then(data => {
      if (data.phone && !user?.phoneNumber) {
        setEditPhone(data.phone);
      }
      if (data.name && !user?.displayName) {
        setEditName(data.name);
      }
    }).catch(console.error);
  }, [user]);

  const handleSaveProfile = async () => {
    setIsSaving(true);
    try {
      const updatedUser = await updateUserAuthProfile(editName, user?.photoURL);
      await updateProfileData({ name: editName, phone: editPhone });
      // update signInUpdate as well just in case
      signInUpdate(user.uid, { name: editName, phone: editPhone });
      
      setUser({ ...updatedUser, phoneNumber: editPhone }); // Update local state for App.js
      setEditOpen(false);
    } catch (e) {
      console.error(e);
      alert("Failed to update profile");
    }
    setIsSaving(false);
  };

  const toggleHandedAmountOption = (e) => {
    const newVal = e.target.checked;
    setUseHandedAmount(newVal);
    updatePreferences({ useHandedAmountForAnalytics: newVal }).catch(console.error);
  };

  return (
    <div className="page-pad fade-in" style={{ paddingBottom: '120px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 24, padding: '12px 4px 8px' }}>
        <h1 style={{ fontSize: 26, margin: 0 }}>Profile & Settings</h1>
      </div>

      {/* Profile Card */}
      <div className="glass-card" style={{ 
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '24px', 
        borderRadius: 20, marginBottom: 24, background: 'var(--bg-card)', border: '1px solid var(--border-subtle)' 
      }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Avatar src={user?.photoURL} alt={user?.displayName} sx={{ width: 72, height: 72, mr: 3, border: '2px solid var(--accent-blue)' }} />
          <div>
            <h2 style={{ fontSize: 20, marginBottom: 4, color: 'var(--text-primary)' }}>{user?.displayName || editName || 'User'}</h2>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: 0 }}>{user?.email}</p>
            {editPhone && <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '4px 0 0 0' }}>{editPhone}</p>}
          </div>
        </div>
        <IconButton onClick={() => setEditOpen(true)} sx={{ color: 'var(--text-secondary)' }}>
          <Edit />
        </IconButton>
      </div>

      <Dialog open={editOpen} onClose={() => !isSaving && setEditOpen(false)} PaperProps={{ style: { background: 'var(--bg-elevated)', color: 'var(--text-primary)', borderRadius: 16 } }}>
        <DialogTitle sx={{ fontFamily: 'Inter', fontWeight: 600 }}>Edit Profile</DialogTitle>
        <DialogContent sx={{ minWidth: { xs: '280px', sm: '360px' } }}>
          <TextField
            autoFocus
            margin="dense"
            label="Display Name"
            type="text"
            fullWidth
            variant="outlined"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            InputProps={{ style: { color: 'var(--text-primary)' } }}
            InputLabelProps={{ style: { color: 'var(--text-secondary)' } }}
            sx={{ mb: 2, mt: 1, '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: 'var(--border-subtle)' }, '&:hover fieldset': { borderColor: 'var(--accent-blue)' } } }}
          />
          <TextField
            margin="dense"
            label="Phone Number"
            type="tel"
            fullWidth
            variant="outlined"
            value={editPhone}
            onChange={(e) => setEditPhone(e.target.value)}
            InputProps={{ style: { color: 'var(--text-primary)' } }}
            InputLabelProps={{ style: { color: 'var(--text-secondary)' } }}
            sx={{ '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: 'var(--border-subtle)' }, '&:hover fieldset': { borderColor: 'var(--accent-blue)' } } }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={() => setEditOpen(false)} disabled={isSaving} sx={{ color: 'var(--text-secondary)', textTransform: 'none' }}>Cancel</Button>
          <Button onClick={handleSaveProfile} disabled={isSaving} variant="contained" sx={{ background: 'var(--accent-blue)', textTransform: 'none' }}>
            {isSaving ? <CircularProgress size={24} color="inherit" /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      <div style={{ paddingLeft: 8, paddingRight: 8 }}>
        <h3 style={{ fontSize: 13, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 12 }}>Subscription</h3>
        
        {/* Subscription Plan */}
        <div 
          onClick={() => navigate('/subscription')}
          style={{ 
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', 
            background: isPremium ? 'rgba(15,212,122,0.05)' : 'rgba(45,132,255,0.05)', 
            padding: '16px 20px', borderRadius: 16, marginBottom: 32,
            border: isPremium ? '1px solid rgba(15,212,122,0.3)' : '1px solid rgba(45,132,255,0.3)', 
            cursor: 'pointer'
          }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--bg-elevated)', padding: '12px 16px', borderRadius: 12, border: '1px solid var(--border-subtle)', flex: 1 }}>
              <Star style={{ color: isPremium ? '#0fd47a' : '#15C358', fontSize: 22 }}/>
              <div>
                <p style={{ margin: 0, fontWeight: 700, fontSize: 15, color: isPremium ? '#0fd47a' : '#15C358', fontFamily: 'Inter' }}>
                {isPremium ? "Pro Plan" : "Basic Plan (Free)"}
              </p>
              <p style={{ margin: 0, fontSize: 12, color: 'var(--text-secondary)', fontFamily: 'Inter' }}>
                {isPremium ? "Active Subscription" : "Tap to Upgrade"}
              </p>
            </div>
          </div>
        </div>
      </div>

        <h3 style={{ fontSize: 13, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 12 }}>Preferences</h3>
        
        {/* Theme Setting */}
        <div style={{ 
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', 
          background: 'var(--bg-card)', padding: '16px 20px', borderRadius: 16, marginBottom: 32,
          border: '1px solid var(--border-subtle)' 
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {theme === 'dark' ? <DarkMode style={{ color: '#15C358', fontSize: 22 }}/> : <LightMode style={{ color: '#ffd166', fontSize: 22 }}/>}
              <div>
                <p style={{ margin: 0, fontWeight: 600, fontSize: 15, color: 'var(--text-primary)', fontFamily: 'Inter' }}>Dark Mode</p>
                <p style={{ margin: 0, fontSize: 12, color: 'var(--text-secondary)', fontFamily: 'Inter' }}>Toggle application theme</p>
              </div>
            </div>
          </div>
          <Switch checked={theme === 'dark'} onChange={toggleTheme} sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#15C358' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#15C358' } }} />
        </div>

        {/* Language Setting */}
        <div style={{ 
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', 
          background: 'var(--bg-card)', padding: '16px 20px', borderRadius: 16, marginBottom: 16,
          border: '1px solid var(--border-subtle)' 
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <Language style={{ color: '#2d84ff', fontSize: 22 }}/>
              <div>
                <p style={{ margin: 0, fontWeight: 600, fontSize: 15, color: 'var(--text-primary)', fontFamily: 'Inter' }}>Language</p>
                <p style={{ margin: 0, fontSize: 12, color: 'var(--text-secondary)', fontFamily: 'Inter' }}>Select your preferred language</p>
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {!isPremium ? (
              <div 
                onClick={() => navigate('/subscription')}
                style={{ 
                  background: 'rgba(255,215,0,0.15)', border: '1px solid rgba(255,215,0,0.4)', 
                  padding: '4px 8px', borderRadius: 4, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 
                }}
              >
                <LockOutlined style={{ color: '#FFD700', fontSize: 12 }} />
                <span style={{ fontSize: 10, fontWeight: 700, color: '#FFD700' }}>PRO</span>
              </div>
            ) : (
              <select 
                value={i18n.language || 'en'} 
                onChange={(e) => {
                  i18n.changeLanguage(e.target.value);
                  updatePreferences({ language: e.target.value }).catch(console.error);
                }}
                style={{
                  background: 'var(--bg-elevated)',
                  color: 'var(--text-primary)',
                  border: '1px solid var(--border-subtle)',
                  borderRadius: '6px',
                  padding: '6px 12px',
                  fontFamily: 'Inter',
                  fontSize: 14,
                  outline: 'none'
                }}
              >
                <option value="en">English</option>
                <option value="ta">தமிழ் (Tamil)</option>
              </select>
            )}
          </div>
        </div>

        {/* Analytics Setting */}
        <div style={{ 
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', 
          background: 'var(--bg-card)', padding: '16px 20px', borderRadius: 16, marginBottom: 32,
          border: '1px solid var(--border-subtle)' 
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ background: 'rgba(255,138,101,0.1)', padding: 10, borderRadius: 12, display: 'flex' }}>
              <AnalyticsOutlined style={{ color: '#ff8a65', fontSize: 22 }}/>
            </div>
            <div>
              <p style={{ margin: 0, fontWeight: 600, fontSize: 15, color: 'var(--text-primary)', fontFamily: 'Inter' }}>Analytics Metric</p>
              <p style={{ margin: 0, fontSize: 12, color: 'var(--text-secondary)', fontFamily: 'Inter' }}>Use Handed Amount instead of Principal for invested metrics</p>
            </div>
          </div>
          <Switch checked={useHandedAmount} onChange={toggleHandedAmountOption} sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#ff8a65' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#ff8a65' } }} />
        </div>

        <h3 style={{ fontSize: 13, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 12 }}>Help & Support</h3>
        
        {/* Support Link */}
        <div 
          onClick={() => navigate('/support')}
          style={{ 
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', 
            background: 'var(--bg-card)', padding: '16px 20px', borderRadius: 16, marginBottom: 32,
            border: '1px solid var(--border-subtle)', cursor: 'pointer'
          }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <HelpOutline style={{ color: '#15C358', fontSize: 22 }}/>
              <div>
                <p style={{ margin: 0, fontWeight: 600, fontSize: 15, color: 'var(--text-primary)', fontFamily: 'Inter' }}>Terminology Guide</p>
                <p style={{ margin: 0, fontSize: 12, color: 'var(--text-secondary)', fontFamily: 'Inter' }}>View Tamil & English terms</p>
              </div>
            </div>
          </div>
        </div>

        <h3 style={{ fontSize: 13, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 12 }}>Legal & Policies</h3>
        
        <div 
          onClick={() => navigate('/terms')}
          style={{ 
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', 
            background: 'var(--bg-card)', padding: '16px 20px', borderRadius: 16, marginBottom: 32,
            border: '1px solid var(--border-subtle)', cursor: 'pointer'
          }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ background: 'rgba(15,212,122,0.1)', padding: 10, borderRadius: 12, display: 'flex' }}>
              <Policy style={{ color: '#0fd47a', fontSize: 22 }}/>
            </div>
            <div>
              <p style={{ margin: 0, fontWeight: 600, fontSize: 15, color: 'var(--text-primary)', fontFamily: 'Inter' }}>Terms & Privacy Policy</p>
              {legalAcceptedVersion && legalAcceptedAt ? (
                 <p style={{ margin: '4px 0 0 0', fontSize: 11, color: '#0fd47a', fontFamily: 'Inter', fontWeight: 600, background: 'rgba(15,212,122,0.1)', padding: '2px 8px', borderRadius: 10, display: 'inline-block' }}>
                   ✅ Accepted {legalAcceptedVersion} on {new Date(legalAcceptedAt).toLocaleDateString()}
                 </p>
              ) : (
                 <p style={{ margin: 0, fontSize: 12, color: 'var(--text-secondary)', fontFamily: 'Inter' }}>Review our legal documents</p>
              )}
            </div>
          </div>
        </div>

        <h3 style={{ fontSize: 13, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 12 }}>Account</h3>
        
        {/* Logout */}
        <Button
            startIcon={<ExitToApp />}
            onClick={() => {
              firebaseGoogleSignOut().then(() => setUser(null));
            }}
            fullWidth
            style={{ 
              background: 'rgba(255,77,109,0.1)', color: 'var(--accent-red)', 
              borderRadius: 16, padding: '16px', textTransform: 'none', 
              fontSize: 16, fontWeight: 600, justifyContent: 'center',
              fontFamily: 'Inter', letterSpacing: '0.01em', border: '1px solid rgba(255,77,109,0.2)'
            }}
        >
            Sign Out
        </Button>
      </div>
    </div>
  );
}